import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as UnassignIcon,
    Router as ReceiverIcon,
    FmdGood as GpsIcon
} from '@mui/icons-material';

import { useApiCall, useMutation } from '../../../hooks/useApi';
import { api, receiverHelpers } from '../../../services/api';
import { useNotification } from '../../common/GlobalNotifications';

const ensureString = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Si' : 'No';
    if (value && typeof value.then === 'function') return fallback;
    if (typeof value === 'object') return fallback;
    return fallback;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const formatReceiverMac = (value) => {
    try {
        return receiverHelpers.formatMacAddress(value);
    } catch (error) {
        return ensureString(value);
    }
};

const VehicleConnectivityTab = ({ vehicle, t, onRefresh }) => {
    const { showError, showSuccess } = useNotification();

    const [receiverDialogOpen, setReceiverDialogOpen] = useState(false);
    const [gpsDialogOpen, setGpsDialogOpen] = useState(false);
    const [createReceiverOpen, setCreateReceiverOpen] = useState(false);
    const [createGpsOpen, setCreateGpsOpen] = useState(false);
    const [newReceiverEntries, setNewReceiverEntries] = useState([{ mac_address: '' }]);
    const [newGpsEntries, setNewGpsEntries] = useState([{ device_id: '', model: '' }]);
    const [receiverCreateError, setReceiverCreateError] = useState('');
    const [gpsCreateError, setGpsCreateError] = useState('');
    const [creatingReceivers, setCreatingReceivers] = useState(false);
    const [creatingGpsDevices, setCreatingGpsDevices] = useState(false);

    const { data: allReceiversData = [], refetch: refetchReceivers } = useApiCall(api.receivers.getAll);
    const { data: allGpsData = [], refetch: refetchGps } = useApiCall(api.gps.getAll);

    const allReceivers = useMemo(() => toArray(allReceiversData), [allReceiversData]);
    const allGps = useMemo(() => toArray(allGpsData), [allGpsData]);
    const vehicleId = ensureString(vehicle?.id);

    const availableReceivers = useMemo(
        () => allReceivers.filter((receiver) => !receiver?.vehicle_id || `${receiver.vehicle_id}` === vehicleId),
        [allReceivers, vehicleId]
    );

    const availableGps = useMemo(
        () => allGps.filter((item) => !item?.vehicle_id || `${item.vehicle_id}` === vehicleId),
        [allGps, vehicleId]
    );

    const vehicleReceivers = useMemo(
        () => allReceivers.filter((receiver) => `${receiver?.vehicle_id || ''}` === vehicleId),
        [allReceivers, vehicleId]
    );

    const vehicleGps = useMemo(
        () => allGps.filter((item) => `${item?.vehicle_id || ''}` === vehicleId),
        [allGps, vehicleId]
    );

    const refreshAll = async () => {
        await Promise.all([refetchReceivers(), refetchGps()]);
        onRefresh?.();
    };

    const resetReceiverCreateState = () => {
        setNewReceiverEntries([{ mac_address: '' }]);
        setReceiverCreateError('');
    };

    const resetGpsCreateState = () => {
        setNewGpsEntries([{ device_id: '', model: '' }]);
        setGpsCreateError('');
    };

    const closeCreateReceiverDialog = () => {
        setCreateReceiverOpen(false);
        resetReceiverCreateState();
    };

    const closeCreateGpsDialog = () => {
        setCreateGpsOpen(false);
        resetGpsCreateState();
    };

    const handleReceiverEntryChange = (index, value) => {
        setReceiverCreateError('');
        const formattedValue = formatReceiverMac(value);
        setNewReceiverEntries((previous) => previous.map((entry, entryIndex) => (
            entryIndex === index ? { mac_address: formattedValue } : entry
        )));
    };

    const handleAddReceiverEntry = () => {
        setReceiverCreateError('');
        setNewReceiverEntries((previous) => [...previous, { mac_address: '' }]);
    };

    const handleRemoveReceiverEntry = (index) => {
        setReceiverCreateError('');
        setNewReceiverEntries((previous) => {
            if (previous.length === 1) {
                return previous;
            }

            return previous.filter((_, entryIndex) => entryIndex !== index);
        });
    };

    const handleGpsEntryChange = (index, field, value) => {
        setGpsCreateError('');
        setNewGpsEntries((previous) => previous.map((entry, entryIndex) => (
            entryIndex === index
                ? { ...entry, [field]: value }
                : entry
        )));
    };

    const handleAddGpsEntry = () => {
        setGpsCreateError('');
        setNewGpsEntries((previous) => [...previous, { device_id: '', model: '' }]);
    };

    const handleRemoveGpsEntry = (index) => {
        setGpsCreateError('');
        setNewGpsEntries((previous) => {
            if (previous.length === 1) {
                return previous;
            }

            return previous.filter((_, entryIndex) => entryIndex !== index);
        });
    };

    const { mutate: assignReceiver } = useMutation(
        ({ receiverId }) => api.receivers.update(receiverId, { vehicle_id: vehicleId || null }),
        {
            onSuccess: async () => {
                showSuccess(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.receptor_asociado_al_vehiculo'));
                await refreshAll();
                setReceiverDialogOpen(false);
            },
            onError: (error) => showError(error.message || t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_se_pudo_asociar_el'))
        }
    );

    const { mutate: unassignReceiver } = useMutation(
        ({ receiverId }) => api.receivers.update(receiverId, { vehicle_id: null }),
        {
            onSuccess: async () => {
                showSuccess(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.receptor_desasociado'));
                await refreshAll();
            },
            onError: (error) => showError(error.message || t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_se_pudo_desasociar_el'))
        }
    );

    const { mutate: assignGps } = useMutation(
        ({ gpsId }) => api.gps.update(gpsId, { vehicle_id: vehicleId || null }),
        {
            onSuccess: async () => {
                showSuccess(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.gps_asociado_al_vehiculo'));
                await refreshAll();
                setGpsDialogOpen(false);
            },
            onError: (error) => showError(error.message || t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_se_pudo_asociar_el'))
        }
    );

    const { mutate: unassignGps } = useMutation(
        ({ gpsId }) => api.gps.update(gpsId, { vehicle_id: null }),
        {
            onSuccess: async () => {
                showSuccess(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.gps_desasociado'));
                await refreshAll();
            },
            onError: (error) => showError(error.message || t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_se_pudo_desasociar_el'))
        }
    );

    const handleCreateReceivers = async () => {
        setReceiverCreateError('');

        const receiversToCreate = newReceiverEntries
            .map((entry) => ({ mac_address: ensureString(entry.mac_address).trim() }))
            .filter((entry) => entry.mac_address);

        if (!receiversToCreate.length) {
            setReceiverCreateError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.agrega_al_menos_un_receptor'));
            return;
        }

        const invalidReceiver = receiversToCreate.find((entry) => !receiverHelpers.validateMacAddress(entry.mac_address));
        if (invalidReceiver) {
            setReceiverCreateError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.la_mac_mac_address_no_tiene', { mac_address: invalidReceiver.mac_address }));
            return;
        }

        const normalizedMacs = receiversToCreate.map((entry) => entry.mac_address.toUpperCase());
        if (new Set(normalizedMacs).size !== normalizedMacs.length) {
            setReceiverCreateError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_puedes_registrar_macs_repetidas'));
            return;
        }

        setCreatingReceivers(true);
        try {
            for (const entry of receiversToCreate) {
                await api.receivers.create({
                    mac_address: entry.mac_address,
                    vehicle_id: vehicleId || null,
                });
            }

            showSuccess(
                receiversToCreate.length === 1
                    ? t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.receptor_creado_y_asociado')
                    : t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.length_receptores_creados_y_asociados', { length: receiversToCreate.length })
            );
            closeCreateReceiverDialog();
            await refreshAll();
        } catch (error) {
            const errorMessage = error.message || t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_se_pudieron_crear_los');
            setReceiverCreateError(errorMessage);
            showError(errorMessage);
        } finally {
            setCreatingReceivers(false);
        }
    };

    const handleCreateGps = async () => {
        setGpsCreateError('');

        const gpsToCreate = newGpsEntries
            .map((entry) => ({
                device_id: ensureString(entry.device_id).trim(),
                model: ensureString(entry.model).trim(),
            }))
            .filter((entry) => entry.device_id);

        if (!gpsToCreate.length) {
            setGpsCreateError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.agrega_al_menos_un_gps'));
            return;
        }

        const normalizedDeviceIds = gpsToCreate.map((entry) => entry.device_id.toLowerCase());
        if (new Set(normalizedDeviceIds).size !== normalizedDeviceIds.length) {
            setGpsCreateError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_puedes_registrar_ids_repetidos'));
            return;
        }

        setCreatingGpsDevices(true);
        try {
            for (const entry of gpsToCreate) {
                await api.gps.create({
                    device_id: entry.device_id,
                    model: entry.model || null,
                    vehicle_id: vehicleId || null,
                });
            }

            showSuccess(
                gpsToCreate.length === 1
                    ? t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.gps_creado_y_asociado')
                    : t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.length_gps_creados_y_asociados', { length: gpsToCreate.length })
            );
            closeCreateGpsDialog();
            await refreshAll();
        } catch (error) {
            const errorMessage = error.message || t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_se_pudieron_crear_los');
            setGpsCreateError(errorMessage);
            showError(errorMessage);
        } finally {
            setCreatingGpsDevices(false);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiverIcon color="secondary" />
                            {i18n.t('receivers.title')}</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => setReceiverDialogOpen(true)}>
                                {i18n.t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.asociar')}</Button>
                            <Button size="small" variant="outlined" onClick={() => setCreateReceiverOpen(true)}>
                                {t('audit.actions.CREATE')}</Button>
                        </Stack>
                    </Stack>

                    <List dense>
                        {vehicleReceivers.map((receiver) => (
                            <ListItem
                                key={receiver.id}
                                secondaryAction={
                                    <Tooltip title={t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.desasociar')}>
                                        <IconButton edge="end" onClick={() => unassignReceiver({ receiverId: receiver.id })}>
                                            <UnassignIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }
                            >
                                <ListItemText
                                    primary={ensureString(receiver.mac_address, t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_mac'))}
                                    secondary={receiver.last_update_at
                                        ? `Ultima actualizacion: ${ensureString(new Date(receiver.last_update_at).toLocaleString(), t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_fecha'))}`
                                        : t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_actualizacion_registrada')}
                                />
                            </ListItem>
                        ))}
                        {vehicleReceivers.length === 0 && <Typography variant="body2" color="text.secondary">{t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_receptores_asociados')}</Typography>}
                    </List>
                </Box>
            </Grid>

            <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GpsIcon color="info" />
                            GPS
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => setGpsDialogOpen(true)}>
                                {i18n.t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.asociar')}</Button>
                            <Button size="small" variant="outlined" onClick={() => setCreateGpsOpen(true)}>
                                {t('audit.actions.CREATE')}</Button>
                        </Stack>
                    </Stack>

                    <List dense>
                        {vehicleGps.map((gps) => (
                            <ListItem
                                key={gps.id}
                                secondaryAction={
                                    <Tooltip title={t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.desasociar')}>
                                        <IconButton edge="end" onClick={() => unassignGps({ gpsId: gps.id })}>
                                            <UnassignIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }
                            >
                                <ListItemText
                                    primary={ensureString(gps.device_id, t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_id'))}
                                    secondary={ensureString(gps.model, t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_modelo'))}
                                />
                            </ListItem>
                        ))}
                        {vehicleGps.length === 0 && <Typography variant="body2" color="text.secondary">{t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.sin_gps_asociados')}</Typography>}
                    </List>
                </Box>
            </Grid>

            <Dialog open={receiverDialogOpen} onClose={() => setReceiverDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{i18n.t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.asociar_receptor_existente')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {availableReceivers.map((receiver) => (
                            <Button
                                key={receiver.id}
                                variant={receiver.vehicle_id === vehicle.id ? 'contained' : 'outlined'}
                                onClick={() => assignReceiver({ receiverId: receiver.id })}
                                disabled={receiver.vehicle_id === vehicle.id}
                                sx={{ justifyContent: 'space-between' }}
                            >
                                {receiver.mac_address}
                                {receiver.vehicle_id === vehicle.id && <Chip size="small" label={t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.asociado')} />}
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiverDialogOpen(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={gpsDialogOpen} onClose={() => setGpsDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{i18n.t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.asociar_gps_existente')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {availableGps.map((gps) => (
                            <Button
                                key={gps.id}
                                variant={gps.vehicle_id === vehicle.id ? 'contained' : 'outlined'}
                                onClick={() => assignGps({ gpsId: gps.id })}
                                disabled={gps.vehicle_id === vehicle.id}
                                sx={{ justifyContent: 'space-between' }}
                            >
                                {gps.device_id}
                                {gps.vehicle_id === vehicle.id && <Chip size="small" label={t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.asociado')} />}
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGpsDialogOpen(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={createReceiverOpen} onClose={closeCreateReceiverDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.crear_receptores_para_este_vehiculo')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {receiverCreateError && (
                            <Alert severity="error">{receiverCreateError}</Alert>
                        )}

                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.todos_los_receptores_que_registres')}</Typography>

                        {newReceiverEntries.map((entry, index) => (
                            <Box
                                key={t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.vehicle-receiver-entry-index', { index: index })}
                                sx={{
                                    p: 2,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {i18n.t('hardcoded.components_schematics_receivereventsmodal.receptor')}{index + 1}
                                    </Typography>
                                    {newReceiverEntries.length > 1 && (
                                        <IconButton color="error" onClick={() => handleRemoveReceiverEntry(index)}>
                                            <UnassignIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>

                                <TextField
                                    fullWidth
                                    label="MAC"
                                    placeholder="00:00:00:00:00:00"
                                    value={entry.mac_address}
                                    onChange={(event) => handleReceiverEntryChange(index, event.target.value)}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    ID: {receiverHelpers.validateMacAddress(entry.mac_address)
                                        ? receiverHelpers.macToDynamoId(entry.mac_address)
                                        : '—'}
                                </Typography>
                            </Box>
                        ))}

                        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddReceiverEntry}>
                            {i18n.t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.agregar_otro_receptor')}</Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateReceiverDialog}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateReceivers}
                        disabled={creatingReceivers || !newReceiverEntries.some((entry) => ensureString(entry.mac_address).trim())}
                    >
                        {t('hardcoded.components_operators_operatorquickcreateform.crear_y_asignar')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={createGpsOpen} onClose={closeCreateGpsDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.crear_gps_para_este_vehiculo')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {gpsCreateError && (
                            <Alert severity="error">{gpsCreateError}</Alert>
                        )}

                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.todos_los_gps_que_registres')}</Typography>

                        {newGpsEntries.map((entry, index) => (
                            <Box
                                key={`vehicle-gps-entry-${index}`}
                                sx={{
                                    p: 2,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        GPS {index + 1}
                                    </Typography>
                                    {newGpsEntries.length > 1 && (
                                        <IconButton color="error" onClick={() => handleRemoveGpsEntry(index)}>
                                            <UnassignIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>

                                <TextField
                                    fullWidth
                                    label={t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.device_id')}
                                    value={entry.device_id}
                                    onChange={(event) => handleGpsEntryChange(index, 'device_id', event.target.value)}
                                />
                                <TextField
                                    fullWidth
                                    label={t('gps.form.model')}
                                    value={entry.model}
                                    onChange={(event) => handleGpsEntryChange(index, 'model', event.target.value)}
                                />
                            </Box>
                        ))}

                        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddGpsEntry}>
                            Agregar otro GPS
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateGpsDialog}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateGps}
                        disabled={creatingGpsDevices || !newGpsEntries.some((entry) => ensureString(entry.device_id).trim())}
                    >
                        {t('hardcoded.components_operators_operatorquickcreateform.crear_y_asignar')}</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default VehicleConnectivityTab;
